import {
	DocumentData,
	DocumentSnapshot,
	FieldValue,
	QueryConstraint,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
	startAfter,
	updateDoc,
} from 'firebase/firestore';

import {db} from '../firebase';
import {currentlyMasterFirebaseUser, getDebug} from './GeneralUse';
import {useUsersStore} from '../stores/UsersStore';

//
// ****************************************************************************************************
// users/{documentId} === firebaseAuthUser.uid
//
// Interact with the Firestore users collection. For functionality regarding the current user with
// regard to Firebase Authentication etc. look at library/FirestoreUser.ts
//
// METHODS
// createSingleUser
// depopulateDummyUsers
// populateDummyUsers
// readFirestoreUser
// readUsersDocuments (NOT EXPORTED)
// readUsersDocumentsLimit
// refreshUsersDocuments
// updateFirestoreUserField
//
const DEBUG = getDebug('users');
// ****************************************************************************************************
//

/*
//
*/
export const createSingleUser = async (uid: string, username?: string) => {
	DEBUG && console.trace(`createSingleUser(${uid})`);

	try {
		const defaultUser = {
			created_at: serverTimestamp(),
			last_edited_at: null,
			last_posted_at: null,
			post_count: 0,
			site_visit_count: 0,
			username: username || null,
			username_last_edited_at: null,
		};

		await setDoc(doc(db, 'users', uid), defaultUser);

		DEBUG && console.trace(`Created users/${uid} as`, defaultUser);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const depopulateDummyUsers = async (count: number) => {
	DEBUG && console.trace(`depopulateDummyUsers(${count})`);

	try {
		if (currentlyMasterFirebaseUser()) {
			let docId = 9000000001;

			for (let i = 0; i < count; i++) {
				DEBUG && console.log(`deleteDoc(doc(db, 'users', '${docId}'))`);
				await deleteDoc(doc(db, 'users', `${docId}`));

				docId++;
			}
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const populateDummyUsers = async (count: number) => {
	DEBUG && console.trace(`populateDummyUsers(${count})`);

	try {
		if (currentlyMasterFirebaseUser()) {
			let docId = 9000000001;

			for (let i = 1; i <= count; i++) {
				DEBUG &&
					console.log(
						`createSingleUser(docId.toString(), 'Mock_User_${i}'))`,
					);
				await createSingleUser(docId.toString(), `Mock_User_${i}`);

				docId++;
			}
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const readFirestoreUser = async (uid: string) => {
	DEBUG && console.trace(`readFirestoreUser('${uid}')`);

	try {
		// Zustand instead of uid?

		const docRef = doc(db, 'users', uid);
		const docSnap = await getDoc(docRef);

		if (docSnap.exists()) {
			return docSnap.data();
		} else {
			DEBUG && console.log(`No document for that uid found`);
			return null;
		}
	} catch (error) {
		DEBUG && console.error(error);
		return null;
	}
};

/*
// We HAVE to specify a valid field.
// We HAVE to specify a valid sort direction.
// We HAVE to specify whether or not we use the query cursor (i.e. do we want to return only the docs STARTING AFTER the lastVisibleUser document location in the query result?)
// We OPTIONALLY specify a limit to the number of docs returned.
//
// How to use (1 = returned document from with the total [collection], 0 = not returned)
// First call: readUsersDocuments('created_at', 'asc', true, 5) ===> returns [1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
// Second call: readUsersDocuments('created_at', 'asc', true, 5) ===> returns [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0]
// Third call: readUsersDocuments('created_at', 'asc', true, 5) ===> returns [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1]
//
// This essentially allows us to do something like: scroll through a list of usernames ordered by the overlying account's creation date, loading another sixteen at a time when we scroll to the
// bottom of the existing container.
*/
const readUsersDocuments = async (
	fieldName: 'created_at' | 'post_count' | 'site_visit_count' | 'username',
	sortDirection: 'asc' | 'desc',
	useCursor: boolean,
	limitCount?: number,
) => {
	DEBUG &&
		console.trace(
			`readUsersDocuments('${fieldName}', '${sortDirection}', ${useCursor}, ${limitCount})`,
		);

	try {
		// Zustand
		const lastVisibleUser = useUsersStore.getState().lastVisibleUser;

		const usersCollection = collection(db, 'users');

		const queryConstraints: QueryConstraint[] = [
			orderBy(fieldName, sortDirection),
		];
		if (useCursor && lastVisibleUser) {
			queryConstraints.push(startAfter(lastVisibleUser));
		} else {
			DEBUG && console.trace(`useCursor disabled or no lastVisibleUser`);
		}
		if (limitCount) {
			queryConstraints.push(limit(limitCount));
		} else {
			DEBUG && console.trace(`no limitCount provided`);
		}

		const q = query(usersCollection, ...queryConstraints);

		const docSnaps = await getDocs(q);
		const docs = docSnaps.docs;

		return docs;
	} catch (error) {
		DEBUG && console.error(error);
		return null;
	}
};

/*
// This is the only way the frontend is allowed to use the readUsersDocuments() method. We neuter the front end, only allowing a CUSTOM BUT NON-NEGOTIABLE limit to the
// number of documents returned. Otherwise we are always returning from earliest created account (should be the whirlwind site creator/owner aka master firebaseAuthUser)
// to the latest, and using the query cursor in the process.
*/
export const readUsersDocumentsLimit = async (limitCount: number) => {
	DEBUG && console.groupCollapsed('Populate Users from Firestore');
	DEBUG && console.trace(`readUsersDocumentsLimit(${limitCount})`);

	try {
		// Zustand
		const users = useUsersStore.getState().users;
		const setUsers = useUsersStore.getState().setUsers;
		const setLastVisibleUser = useUsersStore.getState().setLastVisibleUser;
		const loadedAllUsers = useUsersStore.getState().loadedAllUsers;
		const setLoadedAllUsers = useUsersStore.getState().setLoadedAllUsers;

		if (limitCount >= 1 && !loadedAllUsers) {
			const docs = await readUsersDocuments(
				'created_at',
				'asc',
				true,
				limitCount,
			);

			if (docs && docs.length) {
				let newUsers: DocumentSnapshot<DocumentData, DocumentData>[] = [];
				if (users) {
					newUsers = [...users, ...docs];
				} else {
					newUsers = docs;
				}

				DEBUG && console.trace(`setUsers(`, newUsers, `)`);
				setUsers(newUsers);

				const lastVisibleUserLive = docs[docs.length - 1];
				DEBUG &&
					console.trace(
						`setLastVisibleUser(`,
						lastVisibleUserLive,
						`)
aka`,
						lastVisibleUserLive.data(),
					);
				setLastVisibleUser(lastVisibleUserLive);

				return true;
			} else {
				DEBUG &&
					console.log(
						`docs is either null (error in readUsersDocuments()) or empty`,
					);
				DEBUG && console.trace(`setLastVisibleUser(null)`);
				DEBUG && console.trace(`setLoadedAllUsers(true)`);
				setLastVisibleUser(null);
				setLoadedAllUsers(true);
			}
		} else {
			DEBUG && console.log(`limitCount is < 1 or we have loaded all`);
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG && console.groupEnd();

	return false;
};

/*
//
*/
export const refreshUsersDocuments = async (limitCount: number) => {
	DEBUG && console.trace(`refreshUsersDocuments(${limitCount})`);

	try {
		const setUsers = useUsersStore.getState().setUsers;
		const setLastVisibleUser = useUsersStore.getState().setLastVisibleUser;
		const setLoadedAllUsers = useUsersStore.getState().setLoadedAllUsers;

		setUsers(null);
		setLastVisibleUser(null);
		setLoadedAllUsers(false);

		await readUsersDocumentsLimit(limitCount);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const updateFirestoreUserField = async (
	uid: string,
	field:
		| 'last_posted_at'
		| 'post_count'
		| 'site_visit_count'
		| 'username'
		| 'username_last_edited_at',
	newVal: null | number | string | FieldValue,
) => {
	DEBUG &&
		console.trace(
			`updateFirestoreUserField('${uid}', '${field}', ${newVal})`,
		);

	try {
		// Zustand instead of uid?

		const docRef = doc(db, 'users', uid);

		await updateDoc(docRef, {
			[field]: newVal,
			last_edited_at: serverTimestamp(),
		});
	} catch (error) {
		DEBUG && console.error(error);
		return false;
	}

	return true;
};
